import React from 'react';
import { Link } from 'react-router-dom';
import { launchButton } from '../utils/launchButton';
import { dateTileParts, describeOfficialDate, formatPlanDate, isOverdue, itemTiming } from '../utils/planDates';
import { displayText } from '../utils/displayText';

// One plan item, shared by PathView (interactive) and DraftPlanCard and
// read-only paths (inert). The item type shows in the shape of the row, not
// just a label (decision 10):
//
//   action / admin   checkbox; admin carries a type chip
//   event            date tile instead of a checkbox, "Fixed date" when the
//                    date cannot move; never ticked
//   review           dashed checkpoint row, "Start review" when it is due
//
// Launch controls name the action and the target and use the API's URL
// (launch_url, or the allowlisted external_url) exactly as given; see
// utils/launchButton.js. An inert row shows the same label as text, so a
// draft or a finished path never launches anything.

const MISSING_NOUNS = {
  topic: 'That topic',
  problem: 'That problem',
  interview_type: 'That interview type',
  quiz_topic: 'That quiz topic',
};

function LaunchControl({ button, interactive }) {
  if (!button) return null;
  if (!interactive) {
    return <span className="plan-item-chip plan-item-chip-launch">{button.label}</span>;
  }
  if (button.kind === 'external') {
    return (
      <a className="plan-item-launch" href={button.href} target="_blank" rel="noopener noreferrer">
        {button.label}
      </a>
    );
  }
  return (
    <Link className="plan-item-launch" to={button.href}>
      {button.label} <span aria-hidden="true">→</span>
    </Link>
  );
}

function DateTile({ item }) {
  const parts = dateTileParts(item);
  if (!parts) return <span className="plan-item-date-tile plan-item-date-tile-empty" aria-hidden="true" />;
  return (
    <span className={`plan-item-date-tile${parts.isWindow ? ' plan-item-date-tile-window' : ''}`} aria-hidden="true">
      <span className="plan-item-date-tile-month">{parts.month}</span>
      <span className="plan-item-date-tile-day">{parts.day}</span>
    </span>
  );
}

function Checkbox({ item, interactive, pending, onComplete, onUndo }) {
  const done = (item.progress_status || 'pending') === 'completed';
  const title = displayText(item.title);
  if (!interactive) {
    return <span className={`plan-item-checkbox plan-item-checkbox-inert${done ? ' plan-item-checkbox-done' : ''}`} aria-hidden="true" />;
  }
  return (
    <button
      type="button"
      className={`plan-item-checkbox${done ? ' plan-item-checkbox-done' : ''}`}
      role="checkbox"
      aria-checked={done}
      aria-label={done ? `Mark not done: ${title}` : `Mark done: ${title}`}
      disabled={pending}
      onClick={done ? onUndo : onComplete}
    />
  );
}

export default function PlanItemRow({
  item,
  interactive = false,
  pending = false,
  capabilityNames,
  canStartReview = false,
  onComplete,
  onSkip,
  onUndo,
  onAskReplacement,
  onRevisePlan,
  onStartReview,
}) {
  if (!item) return null;
  const type = ['action', 'admin', 'event', 'review'].includes(item.item_type) ? item.item_type : 'action';
  const status = ['pending', 'completed', 'skipped'].includes(item.progress_status) ? item.progress_status : 'pending';
  const title = displayText(item.title) || 'Untitled item';
  const instruction = displayText(item.instruction);
  const button = type === 'event' || type === 'review' ? null : launchButton(item, capabilityNames);
  const isManual = type === 'action' && !button && !item.capability_key;
  const overdue = interactive && type !== 'event' && isOverdue(item);
  const timing = itemTiming(item);
  const doneOn = status === 'completed' && typeof item.progress_recorded_at === 'string'
    ? formatPlanDate(item.progress_recorded_at.slice(0, 10))
    : null;
  const isWindow = !item.target_date && Boolean(item.window_start);

  const marker = type === 'event'
    ? <DateTile item={item} />
    : type === 'review'
      ? <span className="plan-item-review-mark" aria-hidden="true">✓</span>
      : <Checkbox item={item} interactive={interactive} pending={pending} onComplete={onComplete} onUndo={onUndo} />;

  const missingNoun = MISSING_NOUNS[item.launch_target?.kind] || 'That target';
  const officialDate = describeOfficialDate(item.key_date_current);
  const plannedDate = timing;

  return (
    <div
      className={
        `plan-item-row plan-item-row-${type} plan-item-row-${status}`
        + (overdue ? ' plan-item-row-overdue' : '')
        + (interactive ? '' : ' plan-item-row-inert')
      }
      data-item-id={item.id}
    >
      {marker}
      <div className="plan-item-body">
        <div className="plan-item-title">{title}</div>
        {interactive && instruction && <div className="plan-item-instruction">{instruction}</div>}

        <div className="plan-item-meta">
          {type === 'admin' && <span className="plan-item-chip plan-item-chip-type">Admin</span>}
          {type === 'review' && <span className="plan-item-chip plan-item-chip-type">Checkpoint</span>}
          {type === 'event' && item.date_is_movable === false && (
            <span className="plan-item-chip plan-item-chip-fixed">{isWindow ? 'Fixed window' : 'Fixed date'}</span>
          )}
          {isManual && !item.external_url && <span className="plan-item-chip">Off platform</span>}
          {status === 'completed' && <span className="plan-item-chip plan-item-chip-done">Done{doneOn ? ` ${doneOn}` : ''}</span>}
          {status === 'skipped' && <span className="plan-item-chip">Skipped</span>}
          {overdue && <span className="plan-item-chip plan-item-chip-overdue">Overdue</span>}

          <LaunchControl button={button} interactive={interactive} />

          {type === 'review' && interactive && canStartReview && status === 'pending' && (
            <button type="button" className="plan-item-launch" onClick={() => onStartReview?.(item)}>
              Start review
            </button>
          )}
          {status === 'pending' && type !== 'event' && timing && (
            <span className="plan-item-chip">{type === 'review' ? timing : `By ${timing}`}</span>
          )}
          {type === 'event' && timing && <span className="plan-item-sr-only">{timing}</span>}
          {Number.isInteger(item.estimated_effort_minutes) && item.estimated_effort_minutes > 0 && status === 'pending' && (
            <span className="plan-item-chip">{item.estimated_effort_minutes} min</span>
          )}

          {interactive && status === 'pending' && (type === 'action' || type === 'admin') && onSkip && (
            <button type="button" className="plan-item-text-btn" disabled={pending} onClick={onSkip}>Skip</button>
          )}
          {interactive && status === 'skipped' && onUndo && (
            <button type="button" className="plan-item-text-btn" disabled={pending} onClick={onUndo}>Undo skip</button>
          )}
        </div>

        {button && button.targetMissing && (
          <div className="plan-item-notice" role="note">
            {missingNoun} isn&apos;t in {button.appName} any more, so this opens {button.appName}.
            {interactive && onAskReplacement && (
              <button type="button" onClick={() => onAskReplacement(item, button)}>Ask for a replacement</button>
            )}
          </div>
        )}

        {type === 'event' && item.key_date_changed === true && (
          <div className="plan-item-notice" role="note">
            {officialDate
              ? `The official date is now ${officialDate}.`
              : 'The official date has changed since this plan was made.'}
            {plannedDate ? ` Your plan still says ${plannedDate}.` : ''}
            {interactive && onRevisePlan && (
              <button type="button" onClick={() => onRevisePlan(item)}>Revise plan</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
